import { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import _ from "underscore";

import { SettingsPageWrapper } from "metabase/admin/components/SettingsSection";
import { UpsellCacheConfig } from "metabase/admin/upsells";
import { useListDatabasesQuery } from "metabase/api";
import { DelayedLoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper/DelayedLoadingAndErrorWrapper";
import { Sidesheet } from "metabase/common/components/Sidesheet";
import { Table } from "metabase/common/components/Table";
import type { ColumnItem } from "metabase/common/components/Table/types";
import { usePagination } from "metabase/common/hooks/use-pagination";
import { PLUGIN_CACHING } from "metabase/plugins";
import {
  Button,
  Center,
  FixedSizeIcon,
  Flex,
  Stack,
  Text,
  TextInput,
} from "metabase/ui";
import type { CacheStrategy, CacheableModel } from "metabase-types/api";

import { rootId } from "../constants/simple";
import { useCacheConfigs } from "../hooks/useCacheConfigs";
import { useConfirmIfFormIsDirty } from "../hooks/useConfirmIfFormIsDirty";
import { useSaveStrategy } from "../hooks/useSaveStrategy";
import type { UpdateTargetId } from "../types";
import { getShortStrategyLabel } from "../utils";

import { StrategyForm } from "./StrategyForm";

const PAGE_SIZE = 10;

type DatabaseRow = {
  id: number;
  name: string;
  strategy?: CacheStrategy;
};

export const StrategyEditorForDatabases: React.FC = () => {
  const [targetId, setTargetId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const { page, setPage, resetPage } = usePagination();

  const model: CacheableModel[] = useMemo(() => ["root", "database"], []);

  const {
    configs,
    error: configsError,
    isLoading: areConfigsLoading,
  } = useCacheConfigs({ model });

  const databasesResult = useListDatabasesQuery();
  const databases = useMemo(
    () => databasesResult.data?.data ?? [],
    [databasesResult.data?.data],
  );

  const databaseRows = useMemo<DatabaseRow[]>(() => {
    const rootConfig = _.findWhere(configs ?? [], { model: "root" });

    return databases.map((db) => ({
      id: db.id,
      name: db.name,
      strategy:
        _.findWhere(configs ?? [], { model_id: db.id, model: "database" })
          ?.strategy ?? rootConfig?.strategy,
    }));
  }, [configs, databases]);

  const filteredDatabaseRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return databaseRows;
    }

    return databaseRows.filter((database) =>
      database.name.toLowerCase().includes(normalizedSearch),
    );
  }, [databaseRows, searchText]);

  const total = filteredDatabaseRows.length;
  const paginatedRows = useMemo(
    () =>
      filteredDatabaseRows.slice(
        page * PAGE_SIZE,
        page * PAGE_SIZE + PAGE_SIZE,
      ),
    [filteredDatabaseRows, page],
  );

  const targetConfig = findConfig(configs, targetId);

  const savedStrategy = useMemo(() => {
    const strategy = targetConfig?.strategy;
    if (!strategy) {
      return undefined;
    }
    return { ...strategy };
  }, [targetConfig?.strategy]);

  const {
    askBeforeDiscardingChanges,
    confirmationModal,
    isStrategyFormDirty,
    setIsStrategyFormDirty,
  } = useConfirmIfFormIsDirty();

  const updateTargetId: UpdateTargetId = useCallback(
    (newTargetId, isFormDirty) => {
      if (targetId !== newTargetId) {
        const update = () => {
          setTargetId(newTargetId);
          setIsStrategyFormDirty(false);
        };
        if (isFormDirty) {
          askBeforeDiscardingChanges(update);
        } else {
          update();
        }
      }
    },
    [askBeforeDiscardingChanges, setIsStrategyFormDirty, targetId],
  );

  useEffect(() => {
    resetPage();
  }, [resetPage, searchText]);

  useEffect(() => {
    if (page > 0 && page * PAGE_SIZE >= total) {
      setPage(0);
    }
  }, [page, setPage, total]);

  useEffect(() => {
    if (
      targetId !== null &&
      targetId !== rootId &&
      !filteredDatabaseRows.some((database) => database.id === targetId)
    ) {
      setTargetId(null);
    }
  }, [filteredDatabaseRows, targetId]);

  const targetDatabase = databases.find((db) => db.id === targetId);

  const shouldAllowInvalidation = useMemo(() => {
    if (
      targetId === null ||
      targetId === rootId ||
      savedStrategy?.type === "nocache"
    ) {
      return false;
    }
    const inheritingRootStrategy = ["inherit", undefined].includes(
      savedStrategy?.type,
    );
    const rootConfig = _.findWhere(configs ?? [], { model_id: rootId });
    const inheritingDoNotCache =
      inheritingRootStrategy && !rootConfig?.strategy;
    return !inheritingDoNotCache;
  }, [configs, savedStrategy?.type, targetId]);

  const saveStrategy = useSaveStrategy(targetId, "database");

  const error = configsError || databasesResult.error;
  const loading = areConfigsLoading || databasesResult.isLoading;
  if (error || loading) {
    return <DelayedLoadingAndErrorWrapper error={error} loading={loading} />;
  }

  const tableColumns: ColumnItem[] = [
    { key: "name", name: t`Name`, sortable: false },
    { key: "policy", name: t`Policy`, sortable: false },
  ];

  return (
    <SettingsPageWrapper
      title={t`Database caching`}
      aria-label={t`Data caching settings`}
      description={
        <>
          {t`Speed up queries by caching their results.`}
          <PLUGIN_CACHING.GranularControlsExplanation />
        </>
      }
    >
      {confirmationModal}

      <Stack gap="lg">
        <Stack gap="sm">
          <Text
            size="sm"
            fw={800}
            c="text-secondary"
            tt="uppercase"
            ls="0.08em"
          >
            {t`Databases`}
          </Text>
          <TextInput
            value={searchText}
            onChange={(event) => setSearchText(event.currentTarget.value)}
            placeholder={t`Search databases`}
          />
          <Table<DatabaseRow>
            columns={tableColumns}
            rows={paginatedRows}
            rowRenderer={(item) => (
              <DatabaseRowRenderer
                item={item}
                currentTargetId={targetId}
                updateTarget={updateTargetId}
                isFormDirty={isStrategyFormDirty}
              />
            )}
            emptyBody={<NoResultsTableRow />}
            paginationProps={
              total > PAGE_SIZE
                ? {
                    page,
                    pageSize: PAGE_SIZE,
                    total,
                    showTotal: true,
                    onPageChange: setPage,
                  }
                : undefined
            }
          />
        </Stack>

        <Sidesheet
          isOpen={targetId !== null}
          onClose={() => updateTargetId(null, isStrategyFormDirty)}
          title={targetDatabase?.name ?? t`Caching policy`}
        >
          {targetId !== null && (
            <StrategyForm
              targetId={targetId}
              targetModel="database"
              targetName={targetDatabase?.name || t`Untitled database`}
              setIsDirty={setIsStrategyFormDirty}
              saveStrategy={saveStrategy}
              savedStrategy={savedStrategy}
              shouldAllowInvalidation={shouldAllowInvalidation}
              shouldShowName={false}
              isInSidebar
            />
          )}
        </Sidesheet>
      </Stack>

      <UpsellCacheConfig location="performance-data_cache" />
    </SettingsPageWrapper>
  );
};

const DatabaseRowRenderer = ({
  item,
  currentTargetId,
  updateTarget,
  isFormDirty,
}: {
  item: DatabaseRow;
  currentTargetId: number | null;
  updateTarget: (id: number | null, isFormDirty: boolean) => void;
  isFormDirty: boolean;
}) => (
  <tr>
    <td>
      <Flex align="center" gap="sm">
        <FixedSizeIcon name="database" />
        <Text fw={700}>{item.name}</Text>
      </Flex>
    </td>
    <td>
      <Button
        variant={currentTargetId === item.id ? "filled" : "subtle"}
        onClick={() => updateTarget(item.id, isFormDirty)}
        p={0}
      >
        {getShortStrategyLabel(item.strategy) || t`Use default`}
      </Button>
    </td>
  </tr>
);

const NoResultsTableRow = () => (
  <tr>
    <td colSpan={2}>
      <Center py="xl">
        <Text c="text-secondary">{t`No databases found.`}</Text>
      </Center>
    </td>
  </tr>
);

function findConfig(
  configs: { model_id: number; strategy: CacheStrategy }[] | undefined,
  targetId: number | null,
) {
  return _.findWhere(configs ?? [], {
    model_id: targetId ?? undefined,
  });
}
